import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { SignatureAreaSections } from '.';
import { SignatureDocument } from '../../../../model/signature-area/signature-document';
import { addAllBasicExtraReducers } from '../../../../utils/action-builder-utils';
import { array2Map } from '../../../../utils/utils';
import {
  loadBundleDocumentDetails,
  loadOrdineRto,
  rejectProposal,
  retrieveBundleStatus,
  signatureArea,
} from './signature-area-thunk';
import { SignatureAreaState } from './signature-area-types';

const initialState: SignatureAreaState = {
  documents: {
    items: {},
    all: [],
    selected: null,
  },
  selectedModuleId: null,
  signatureAreaRequestStatus: 'idle',
  selectedSection: 'ALL',
  rejectProposalRequestStatus: 'idle',
  bundleStatusRequestStatus: 'idle',
  selectedDocumentClone: null,
};

const SignatureAreaSlice = createSlice({
  name: 'features/signature.area',
  initialState: () => {
    return initialState;
  },
  reducers: {
    //reset state SignatureAreaSlice
    resetSignatureAreaState() {
      return initialState;
    },
    clearSignatureAreaError(state) {
      state.signatureAreaError = undefined;
      state.signatureAreaRequestStatus = 'idle';
    },
    clearRejectProposalError(state) {
      state.rejectProposalError = undefined;
      state.rejectProposalRequestStatus = 'idle';
    },
    clearBundleStatus(state) {
      state.bundleStatusError = undefined;
      state.bundleStatusRequestStatus = 'idle';
      state.bundleStatus = undefined;
    },
    setSelectedDocumentState(state, action: PayloadAction<string>) {
      state.documents.selected = action.payload;
    },
    clearSelectedDocuments(state) {
      state.documents.selected = undefined;
    },
    setSelectedModuleIdState(state, action: PayloadAction<string>) {
      state.selectedModuleId = action.payload;
    },
    updateSelectedSection(state, action: PayloadAction<SignatureAreaSections>) {
      state.selectedSection = action.payload;
    },
    setSelectedDocumentClone(state, action: PayloadAction<SignatureDocument>) {
      state.selectedDocumentClone = action.payload;
    },
    clearSelectedDocumentClone(state) {
      state.selectedDocumentClone = null;
    },
  },
  extraReducers: (builder) => {
    //Insert PhoneNumber
    builder.addCase(signatureArea.pending, (state) => {
      state.signatureAreaRequestStatus = 'loading';
    });
    builder.addCase(
      signatureArea.fulfilled,
      (state, action: PayloadAction<SignatureDocument[]>) => {
        const selectedDocumentId = state.documents.selected;

        state.signatureAreaRequestStatus = 'succeeded';
        state.documents = array2Map<SignatureDocument>(action.payload, 'id');

        if (
          state.documents.all.findIndex(
            (documentId) => documentId === selectedDocumentId
          ) > 0
        ) {
          state.documents.selected = selectedDocumentId;
        }
      }
    );
    builder.addCase(signatureArea.rejected, (state, action) => {
      state.signatureAreaError = action.payload;
      state.signatureAreaRequestStatus = 'failed';
    });
    addAllBasicExtraReducers(builder, rejectProposal, 'rejectProposal');
    addAllBasicExtraReducers(builder, retrieveBundleStatus, 'bundleStatus');
    builder.addCase(loadBundleDocumentDetails.pending, (state, action) => {
      const bundleId = action.meta.arg;
      if (state.documents.items[bundleId]) {
        state.documents.items[bundleId].isModulesLoading = true;
      }
    });
    builder.addCase(loadBundleDocumentDetails.fulfilled, (state, action) => {
      const { bundleId, modules, practiceNumber, type } = action.payload;
      const doc = state.documents.items[bundleId];
      if (doc) {
        doc.isModulesLoading = false;
        doc.modules = modules;
        if (practiceNumber) {
          doc.practiceNumber = practiceNumber;
        }
        if (type) {
          doc.type = type;
        }
      }
    });
    builder.addCase(loadBundleDocumentDetails.rejected, (state, action) => {
      const bundleId = action.meta.arg;
      if (state.documents.items[bundleId]) {
        state.documents.items[bundleId].isModulesLoading = false;
      }
      state.signatureAreaError = action.payload || 'GENERIC_ERROR';
    });

    builder.addCase(loadOrdineRto.pending, (state, action) => {
      const { bundleId } = action.meta.arg;
      if (state.documents.items[bundleId]) {
        state.documents.items[bundleId].isRtoDetailsLoading = true;
      }
    });
    builder.addCase(loadOrdineRto.fulfilled, (state, action) => {
      const { bundleId, rtoResult } = action.payload;
      const doc = state.documents.items[bundleId];
      if (doc) {
        doc.isRtoDetailsLoading = false;
        if (rtoResult) {
          doc.modules = doc.modules?.map((module: any) => {
            if (module.type === 'RtoImiModule') {
              return {
                ...module,
                moduleInformation: module.moduleInformation?.map(
                  (info: any) => ({
                    ...info,
                    tipoProdotto: rtoResult.tipoProdotto,
                    parametroTempo: rtoResult.parametroTempo,
                    parametroPrezzo: rtoResult.parametroPrezzo,
                    limitePrezzo: rtoResult.limitePrezzo?.toString(),
                    isin: rtoResult.isin,
                  })
                ),
                moduleChecks: {
                  ...module.moduleChecks,
                  appropriatenessOutcome: rtoResult.flagAppropriato
                    ? 'Appropriato'
                    : 'Non Appropriato',
                  complex: rtoResult.complessita,
                  ballIn: rtoResult.bailIn !== 'Non applicabile',
                  illiquid: rtoResult.illiquidity !== 'No',
                  conflictOperation: rtoResult.flagConflittoInteresse,
                },
              };
            }
            return module;
          });
        }
      }
    });
    builder.addCase(loadOrdineRto.rejected, (state, action) => {
      const { bundleId } = action.meta.arg;
      if (state.documents.items[bundleId]) {
        state.documents.items[bundleId].isRtoDetailsLoading = false;
      }
    });
  },
});

export const signatureAreaActions = SignatureAreaSlice.actions;

export default SignatureAreaSlice.reducer;
